/**
 * webAnalytics
 *
 * Google Analytics 4 is a WEBSITE-only integration. The tag used to live as a
 * static <script> in public/index.html, which meant it also executed inside
 * the Capacitor iOS/Android WebView on every launch. It is now injected at
 * boot, and only when the app is NOT running inside a native Capacitor shell.
 *
 * Native apps therefore never request googletagmanager.com or
 * google-analytics.com, and no ATT prompt is needed for analytics.
 */
import { isNativePlatform } from '../utils/providerAuth.js';

export const GA4_MEASUREMENT_ID = 'G-WDF6RTC07Z';
export const GA4_SCRIPT_URL = 'https://www.googletagmanager.com/gtag/js';

/** Headless prerender (puppeteer) must not register page views or snapshot the tag. */
export function isHeadlessRenderer(userAgent) {
  return /HeadlessChrome|Prerender/i.test(String(userAgent || ''));
}

export function shouldLoadWebAnalytics({ capacitor, userAgent } = {}) {
  if (isNativePlatform(capacitor)) return false;
  if (isHeadlessRenderer(userAgent)) return false;
  return true;
}

function defaultCapacitor() {
  try {
    // eslint-disable-next-line global-require
    return require('@capacitor/core').Capacitor;
  } catch {
    return null;
  }
}

/**
 * Idempotent. Returns true when GA4 is active for this page, false when
 * intentionally skipped (native shell / headless).
 */
export function installWebAnalytics(deps = {}) {
  const win = deps.window !== undefined ? deps.window : (typeof window !== 'undefined' ? window : null);
  const doc = deps.document !== undefined ? deps.document : (win ? win.document : null);
  const capacitor = deps.capacitor !== undefined ? deps.capacitor : defaultCapacitor();
  const measurementId = deps.measurementId || GA4_MEASUREMENT_ID;
  const userAgent = deps.userAgent !== undefined ? deps.userAgent : (win && win.navigator ? win.navigator.userAgent : '');

  if (!win || !doc) return false;
  if (!shouldLoadWebAnalytics({ capacitor, userAgent })) return false;

  win.dataLayer = win.dataLayer || [];
  if (typeof win.gtag !== 'function') {
    win.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      win.dataLayer.push(arguments);
    };
  }
  if (!win.__bstGa4Configured) {
    win.gtag('js', new Date());
    win.gtag('config', measurementId);
    win.__bstGa4Configured = measurementId;
  }
  if (!doc.querySelector(`script[src^="${GA4_SCRIPT_URL}"]`)) {
    const script = doc.createElement('script');
    script.async = true;
    script.src = `${GA4_SCRIPT_URL}?id=${encodeURIComponent(measurementId)}`;
    (doc.head || doc.body).appendChild(script);
  }
  return true;
}
